import React, { useEffect, useState, useRef } from "react";
import { Button, Form, Modal, ProgressBar } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// actions
import actions from "../../store/actions";

// helper
import { ALLOWED_WORKBOOK_FILE_TYPES, humanFileSize } from "../../helpers/constants";
import { UploadIcn } from "Assets/icons";
import { ErrorMessage, Field, Formik } from "formik";
import { useFetchStocksQuery } from "store/services/user.service";
import { uploadStockSchema } from "helpers/yup_validator";
import { useUploadWorkbookMutation } from "store/services/workbook.service";

const UploadStockPopup = ({ stockData, setStockData }) => {
  const progressRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState("1");
  const [file, setFile] = useState(null);
  const [progress, setprogress] = useState(0);

  const {data: stockList, isLoading: loadingStocks} = useFetchStocksQuery();
  const [ uploadWorkbook ] = useUploadWorkbookMutation();

  useEffect(() => {
    return () => {
      setStep("1");
      setprogress(0);
      setFile(null);

      if (progressRef.current) {
        clearInterval(progressRef.current);
        progressRef.current = null;
      }
    };
  }, [stockData]);

  const handleStockData = () => setStockData(!stockData);

  const handleFileChange = (evt) => {
    const file = evt.target.files[0];
    if(file && !ALLOWED_WORKBOOK_FILE_TYPES.includes(file.type)) {
      swal("Invalid file type");
      evt.target.value = ""
      return;
    }
    setFile(file);
  };

  const handleImport = async (values) => {
    try {
      setStep("2");
      setprogress(10);

      progressRef.current = setInterval(() => {
        setprogress((prevState) => prevState + 2.2);
      }, 1000);

      const fileNameWithoutExtension = file.name.split('.').slice(0, -1).join('.');

      let payload = {
        file,
        file_name: fileNameWithoutExtension,
        stock: values?.stock,
      }

      let response = await uploadWorkbook(payload).unwrap();
      if(response?.data) {
        setprogress(100);
        handleStockData();
        window.location = `/workbook/${response.data}`;
      }
    } catch(err) {
      console.error("Error uploading workbook", err);
      setprogress(0);
      setStep("1");
      if (progressRef.current) {
        clearInterval(progressRef.current);
        progressRef.current = null;
      }
    }
  };

  return (
    <Modal
      className="stockData"
      show={stockData}
      onHide={handleStockData}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Body>
        <div className="topHeader d-flex align-items-start justify-content-between gap-10 pb-2">
          <h5 className="m-0 fw-sbold themeClr">Upload Stock Data</h5>

          <Button
            onClick={handleStockData}
            className="border-0 p-0"
            variant="transparent"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M14.34 -0.000732422C17.73 -0.000732422 20 2.37927 20 5.91927V14.0903C20 17.6203 17.73 19.9993 14.34 19.9993H5.67C2.28 19.9993 0 17.6203 0 14.0903V5.91927C0 2.37927 2.28 -0.000732422 5.67 -0.000732422H14.34ZM13.01 6.97027C12.67 6.62927 12.12 6.62927 11.77 6.97027L10 8.74927L8.22 6.97027C7.87 6.62927 7.32 6.62927 6.98 6.97027C6.64 7.31027 6.64 7.87027 6.98 8.20927L8.76 9.99027L6.98 11.7603C6.64 12.1103 6.64 12.6603 6.98 12.9993C7.15 13.1693 7.38 13.2603 7.6 13.2603C7.83 13.2603 8.05 13.1693 8.22 12.9993L10 11.2303L11.78 12.9993C11.95 13.1803 12.17 13.2603 12.39 13.2603C12.62 13.2603 12.84 13.1693 13.01 12.9993C13.35 12.6603 13.35 12.1103 13.01 11.7703L11.23 9.99027L13.01 8.20927C13.35 7.87027 13.35 7.31027 13.01 6.97027Z"
                fill="#EB5757"
              />
            </svg>
          </Button>
        </div>

        <div className="cardBody py-3">
          <p className="m-0">
            Stock data which can be uploaded in any of the below forms.
          </p>

          <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center flex-wrap gap-10">
            <li className=" themeClr">MetaStock (.dat)</li>
            <li className=" themeClr">Format (.csv)</li>
            <li className=" themeClr">API Stock Provider</li>
          </ul>

          {step === "1" && (
            <Formik
              initialValues={{
                stock: "",
              }}
              validationSchema={uploadStockSchema}
              onSubmit={handleImport}
            >
              {({ handleSubmit, values, errors }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="py-2">
                    {!file ? (
                      <div className="uploadFile position-relative p-3 text-center">
                        <input
                          type="file"
                          className="file position-absolute w-100 h-100"
                          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .dat"
                          onChange={handleFileChange}
                          required
                        />
                        <div className="content">
                          <span className="icn mx-auto my-2">
                            <UploadIcn />
                          </span>

                          <h6 className="m-0">
                            {" "}
                            <span className="themeClr fw-sbold">
                              Click to Upload
                            </span>{" "}
                            or drag and drop
                          </h6>
                          <p className="m-0"> (Max. File size: 25 MB)</p>
                        </div>
                      </div>
                    ):(
                      <div className="">
                        <div>
                          <p className="m-0">{file?.name}</p>
                          <p className="m-0">Size: {humanFileSize(file?.size)}</p>
                        </div>
                        <div>
                          <Field as="select" name="stock" className="form-control">
                            <option value="">Select Stock</option>
                            {stockList?.length && stockList.map((stock => {
                              if(!stock?.ticker_name) {
                                return null;
                              }
                              return <option value={stock._id} key={stock._id}>{ stock.ticker_name } - { stock.stock_name }</option>  
                            }))}
                          </Field>
                          <ErrorMessage
                            name="stock"
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </div>
                    )}
                    <div className="btnWrpper mt-4">
                      <Button
                        type="submit"
                        className="d-flex align-items-center justify-content-center commonBtn w-100 borderedBtn"
                      >
                        Import
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {step === "2" && !!file && (
            <div className="py-2">
              <div className="progressCard py-2 px-3">
                <div className="d-flex align-items-start justify-content-between top mb-2   ">
                  <div className="left d-flex align-items-start">
                    <span className="icn me-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M12.5003 18.9584H7.50033C2.97533 18.9584 1.04199 17.0251 1.04199 12.5001V7.50008C1.04199 2.97508 2.97533 1.04175 7.50033 1.04175H11.667C12.0087 1.04175 12.292 1.32508 12.292 1.66675C12.292 2.00841 12.0087 2.29175 11.667 2.29175H7.50033C3.65866 2.29175 2.29199 3.65841 2.29199 7.50008V12.5001C2.29199 16.3417 3.65866 17.7084 7.50033 17.7084H12.5003C16.342 17.7084 17.7087 16.3417 17.7087 12.5001V8.33342C17.7087 7.99175 17.992 7.70842 18.3337 7.70842C18.6753 7.70842 18.9587 7.99175 18.9587 8.33342V12.5001C18.9587 17.0251 17.0253 18.9584 12.5003 18.9584Z"
                          fill="#202020"
                        />
                        <path
                          d="M18.3337 8.9584H15.0003C12.1503 8.9584 11.042 7.85007 11.042 5.00007V1.66674C11.042 1.41674 11.192 1.18341 11.4253 1.09174C11.6587 0.991739 11.9253 1.05007 12.1087 1.22507L18.7753 7.89174C18.9503 8.06674 19.0087 8.34174 18.9087 8.57507C18.8087 8.8084 18.5837 8.9584 18.3337 8.9584ZM12.292 3.17507V5.00007C12.292 7.15007 12.8503 7.70841 15.0003 7.70841H16.8253L12.292 3.17507Z"
                          fill="#202020"
                        />
                        <path
                          d="M10.833 11.4583H5.83301C5.49134 11.4583 5.20801 11.1749 5.20801 10.8333C5.20801 10.4916 5.49134 10.2083 5.83301 10.2083H10.833C11.1747 10.2083 11.458 10.4916 11.458 10.8333C11.458 11.1749 11.1747 11.4583 10.833 11.4583Z"
                          fill="#202020"
                        />
                        <path
                          d="M9.16634 14.7917H5.83301C5.49134 14.7917 5.20801 14.5084 5.20801 14.1667C5.20801 13.8251 5.49134 13.5417 5.83301 13.5417H9.16634C9.50801 13.5417 9.79134 13.8251 9.79134 14.1667C9.79134 14.5084 9.50801 14.7917 9.16634 14.7917Z"
                          fill="#202020"
                        />
                      </svg>
                    </span>

                    <div className="content">
                      <p className="m-0">{file.name}</p>
                      <span className="text-muted">
                        {humanFileSize(file.size)}
                      </span>
                    </div>
                  </div>
                </div>

                <ProgressBar now={progress} />
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default UploadStockPopup;
